import type { CompanyPatchPayload } from "@repo/app-types/entities/company";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import { IconChevronRight, IconX } from "@tabler/icons-react";
import hljs from "highlight.js/lib/core";
import typescript from "highlight.js/lib/languages/typescript";
import xml from "highlight.js/lib/languages/xml";
import "highlight.js/styles/github.css";
import {
  type ChangeEvent,
  type FC,
  type KeyboardEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Button as AriaButton,
  Disclosure,
  DisclosurePanel,
  Heading,
} from "react-aria-components";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";
import { getAxiosInstanceWithAuth } from "../../../api/axiosConfig.js";
import { useUpdateCompanyMutation } from "../../../api/entities/company.js";
import { appPaths } from "../../../app/appPaths.js";
import { useAppDispatch, useAppSelector } from "../../../hooks/hook.js";
import { selectCompany } from "../../company/companySlice.js";
import {
  NotificationType,
  showNotification,
} from "../../notificationsOverlay/notificationsSlice.js";
import { settingsSections } from "../settingsSections.js";

hljs.registerLanguage("typescript", typescript);
hljs.registerLanguage("xml", xml);

function highlightElement(element: HTMLElement | null) {
  if (element) {
    hljs.highlightElement(element);
  }
}

const WIDGET_TOKEN_CODE = `// On your application server
const response = await fetch(
  "https://api.alpharun.com/api/v1/live-coaching/widget-tokens",
  {
    method: "POST",
    headers: {
      // This API key should never leave your server
      "Authorization": "Bearer YOUR_API_KEY",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      // Use your existing authentication mechanism to identify the rep
      teammate_email: req.authenticated_user.email,
    }),
  },
);

// Token is valid for 15 minutes
const { widget_token } = await response.json();
`;

const EMBED_IFRAME_CODE = `const iframe = document.createElement("iframe");
iframe.src = "https://live-coaching-widget.alpharun.com/v1";
iframe.style.width = "500px";
iframe.style.height = "400px";
iframe.style.border = "none";
document.getElementById("coaching-container").appendChild(iframe);

// Wait for the widget to load, then send the token
window.addEventListener("message", (event) => {
  if (
    event.origin === "https://live-coaching-widget.alpharun.com" &&
    event.data?.type === "loaded"
  ) {
    iframe.contentWindow.postMessage(
      {
        type: "token_changed",
        widget_token: widget_token
      },
      "https://live-coaching-widget.alpharun.com",
    );
  }
});`;

const ANNOUNCE_CALL_CODE = `// On your server, such as in a Twilio webhook
const response = await fetch(
  "https://api.alpharun.com/api/v1/live-coaching/calls",
  {
    method: "POST",
    headers: {
      "Authorization": "Bearer YOUR_API_KEY",
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      teammate_email: "agent@yourcompany.com",
      // May be omitted to use playbook routing rules
      playbook_id: "optional-playbook-uuid",
      // Used to identify the incoming Twilio media stream
      origin: {
        type: "twilio",
        account_sid: "ACxxxxx",
        call_sid: "CAxxxxx",
      },
    }),
  },
);

const { call_id, call_token } = await response.json();`;

const FORWARD_AUDIO_CODE = `import twilio from "twilio";

const twiml = new twilio.twiml.VoiceResponse();

// Start streaming audio to Alpharun for live coaching
const start = twiml.start();
const stream = start.stream({
  track: "both_tracks",
  url: "wss://live-coaching-realtime.alpharun.com/v1/twilio-stream",
});
// The call token must match the Account SID and Call SID that was announced
stream.parameter({ name: "call_token", value: call_token });

// Connect the call to a rep
const dial = twiml.dial();
dial.client("rep-identity");

twiml.toString();
`;

export const LiveCoachingSettingsSection: FC = () => {
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const [updateCompany] = useUpdateCompanyMutation();

  const currentDomains = useMemo(
    () => company?.settings.live_coaching?.frame_ancestors_domains ?? [],
    [company?.settings.live_coaching],
  );

  const [domainInput, setDomainInput] = useState("");
  const [domains, setDomains] = useState<readonly string[]>(currentDomains);
  const [widgetToken, setWidgetToken] = useState<string | null>(null);
  const [isLoadingToken, setIsLoadingToken] = useState(true);

  // Sync domains when company data changes externally
  useEffect(() => {
    setDomains(currentDomains);
  }, [currentDomains]);

  const saveDomains = useCallback(
    async (newDomains: readonly string[]) => {
      const payload: CompanyPatchPayload = {
        settings: {
          live_coaching:
            newDomains.length > 0
              ? { frame_ancestors_domains: [...newDomains] }
              : null,
        },
      };

      try {
        await updateCompany(payload).unwrap();
        showNotification(dispatch, {
          id: `live-coaching-domains-${Date.now()}`,
          primaryMessage: "Domains updated",
          type: NotificationType.SUCCESS,
        });
      } catch {
        showNotification(dispatch, {
          id: `live-coaching-domains-${Date.now()}`,
          primaryMessage: "Failed to update domains",
          type: NotificationType.FAILURE,
        });
      }
    },
    [updateCompany, dispatch],
  );

  const addDomain = useCallback(async () => {
    const trimmed = domainInput.trim();
    if (!trimmed || domains.includes(trimmed)) return;
    if (domains.length >= 20) {
      showNotification(dispatch, {
        id: `live-coaching-domains-limit-${Date.now()}`,
        primaryMessage: "Maximum of 20 domains allowed",
        type: NotificationType.FAILURE,
      });
      return;
    }

    const newDomains = [...domains, trimmed];
    setDomains(newDomains);
    setDomainInput("");
    await saveDomains(newDomains);
  }, [domainInput, domains, dispatch, saveDomains]);

  const removeDomain = useCallback(
    async (domain: string) => {
      const newDomains = domains.filter((d) => d !== domain);
      setDomains(newDomains);
      await saveDomains(newDomains);
    },
    [domains, saveDomains],
  );

  // Fetch a widget token on mount for the sidebar preview
  useEffect(() => {
    let cancelled = false;

    async function fetchWidgetToken() {
      try {
        const axios = await getAxiosInstanceWithAuth();
        const { data } = await axios.post("/live-coaching/widget-tokens");
        if (!cancelled) {
          setWidgetToken(data.widget_token);
        }
      } catch {
        if (!cancelled) {
          showNotification(dispatch, {
            id: `live-coaching-try-it-${Date.now()}`,
            primaryMessage: "Failed to generate widget token",
            type: NotificationType.FAILURE,
          });
        }
      } finally {
        if (!cancelled) {
          setIsLoadingToken(false);
        }
      }
    }

    void fetchWidgetToken();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  if (!company) return null;

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        width={PageWidth.Form}
        title={settingsSections.liveCoaching.label}
      />

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="flex-1 space-y-6 overflow-y-auto px-8 pt-2 pb-6">
          {/* Contact center integration */}
          <IntegrationDisclosure title="Contact center integration">
            <p className="text-sm text-foreground">
              Embed a live coaching widget in your browser-based contact center
              application to provide real-time guidance to your team during
              calls.
            </p>

            {/* Domain management */}
            <div className="mt-6">
              <Label size={Size.SMALL}>Allowed domains</Label>
              <p className="mt-1 text-xs text-muted-foreground">
                Add the domains where you will embed the coaching widget. These
                are required for the iframe to load in your application.
              </p>

              <div className="mt-3 flex items-center gap-2">
                <Input
                  value={domainInput}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setDomainInput(e.target.value)
                  }
                  placeholder="e.g. app.yourcompany.com"
                  onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void addDomain();
                    }
                  }}
                  maxLength={100}
                />
                <Button
                  variant={ButtonVariant.Secondary}
                  onClick={() => void addDomain()}
                >
                  Add
                </Button>
              </div>

              {domains.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {domains.map((domain) => (
                    <span
                      key={domain}
                      className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2.5 py-1 text-sm text-gray-700"
                    >
                      {domain}
                      <IconButton
                        variant={ButtonVariant.Tertiary}
                        icon={<IconX className="h-3.5 w-3.5" />}
                        onClick={() => void removeDomain(domain)}
                      />
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Integration instructions */}
            <div className="mt-8">
              <Label size={Size.SMALL}>Integration instructions</Label>

              <ol className="mt-3 ml-5 list-outside list-decimal space-y-6 text-sm text-foreground">
                <li>
                  <span className="font-medium">Set up an API key</span>
                  <p className="mt-1 text-foreground">
                    Create an API key in the{" "}
                    <Link
                      to={appPaths.settingsDevelopers.build()}
                      className="text-indigo-600 hover:text-indigo-500"
                    >
                      API keys settings
                    </Link>
                    .
                  </p>
                </li>

                <li>
                  <span className="font-medium">Generate widget tokens</span>
                  <p className="mt-1 text-foreground">
                    Every time the widget is loaded in your contact center
                    application, use the widget token API endpoint from your
                    server to generate a unique token identifying which
                    teammate&apos;s calls to show in the widget.
                  </p>
                  <CodeBlock code={WIDGET_TOKEN_CODE} language="typescript" />
                </li>

                <li>
                  <span className="font-medium">Embed the widget</span>
                  <p className="mt-1 text-foreground">
                    Add the widget to your page as an iframe and pass the token
                    to the widget to start showing live coaching for the
                    teammate.
                  </p>
                  <CodeBlock code={EMBED_IFRAME_CODE} language="typescript" />
                </li>
              </ol>
            </div>
          </IntegrationDisclosure>

          {/* Twilio integration */}
          <IntegrationDisclosure title="Twilio integration">
            <p className="text-sm text-foreground">
              Connect Twilio calls to Alpharun for live coaching by streaming
              the rep and caller audio.
            </p>

            <ol className="mt-6 ml-5 list-outside list-decimal space-y-6 text-sm text-foreground">
              <li>
                <span className="font-medium">Set up an API key</span>
                <p className="mt-1 text-foreground">
                  Create an API key in the{" "}
                  <Link
                    to={appPaths.settingsDevelopers.build()}
                    className="text-indigo-600 hover:text-indigo-500"
                  >
                    API keys settings
                  </Link>
                  .
                </p>
              </li>

              <li>
                <span className="font-medium">Announce the call</span>
                <p className="mt-1 text-foreground">
                  Before forwarding audio, announce the call to Alpharun to get
                  a call token.
                </p>
                <CodeBlock code={ANNOUNCE_CALL_CODE} language="typescript" />
              </li>

              <li>
                <span className="font-medium">Forward audio</span>
                <p className="mt-1 text-foreground">
                  When connecting the call to a rep (such as in a Twilio webhook
                  or Twilio TaskRouter assignment callback), use the following
                  TwiML to stream the call audio to Alpharun and start live
                  coaching.
                </p>
                <CodeBlock code={FORWARD_AUDIO_CODE} language="typescript" />
                <p className="mt-1 text-foreground">
                  Note: since Alpharun will not be able to validate the Twilio
                  request signature, the media stream endpoint will only ever be
                  used to receive audio and will never be used to send anything
                  back.
                </p>
              </li>
            </ol>
          </IntegrationDisclosure>
        </div>

        <TryItSidebar
          isLoadingToken={isLoadingToken}
          widgetToken={widgetToken}
        />
      </div>
    </div>
  );
};

const IntegrationDisclosure: FC<{
  title: string;
  children: ReactNode;
}> = ({ title, children }) => (
  <Disclosure className="">
    <Heading level={3} className="flex items-center">
      <AriaButton
        slot="trigger"
        className="group flex items-center gap-1 text-base font-semibold text-foreground"
      >
        <IconChevronRight className="size-5 text-muted-foreground transition-transform group-aria-expanded:rotate-90" />
        {title}
      </AriaButton>
    </Heading>

    <DisclosurePanel>
      <div className="mt-4">{children}</div>
    </DisclosurePanel>
  </Disclosure>
);

const CodeBlock: FC<{ code: string; language: string }> = ({
  code,
  language,
}) => (
  <pre className="mt-2 overflow-x-auto rounded-md bg-gray-50 p-4 text-xs leading-5">
    <code ref={highlightElement} className={twMerge(`language-${language}`)}>
      {code}
    </code>
  </pre>
);

const TryItSidebar: FC<{
  isLoadingToken: boolean;
  widgetToken: string | null;
}> = ({ isLoadingToken, widgetToken }) => (
  <div className="flex h-full w-[400px] shrink-0 flex-col overflow-hidden border-l border-border-light bg-white">
    <div className="border-b border-border-light px-4 py-3">
      <Label size={Size.SMALL}>Try it</Label>
    </div>

    {/* Widget preview */}
    <div className="flex-1 overflow-hidden">
      {isLoadingToken ? (
        <div className="flex h-full items-center justify-center">
          <p className="text-sm text-muted-foreground">Loading widget...</p>
        </div>
      ) : widgetToken ? (
        <WidgetPreview widgetToken={widgetToken} />
      ) : (
        <div className="flex h-full items-center justify-center">
          <p className="text-sm text-red-500">Failed to generate token.</p>
        </div>
      )}
    </div>

    {/* Call controls */}
    <CallControls />
  </div>
);

const WIDGET_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:3033/v1"
    : "https://live-coaching-widget.alpharun.com/v1";

const WIDGET_ORIGINS = new Set([
  "https://live-coaching-widget.alpharun.com",
  "http://localhost:3033",
]);

const WidgetPreview: FC<{ widgetToken: string }> = ({ widgetToken }) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!WIDGET_ORIGINS.has(event.origin)) return;

      if (event.data?.type === "loaded") {
        iframeRef.current?.contentWindow?.postMessage(
          { type: "token_changed", widget_token: widgetToken },
          event.origin,
        );
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [widgetToken]);

  return (
    <iframe
      ref={iframeRef}
      src={WIDGET_URL}
      className="h-full w-full"
      title="Live coaching widget"
    />
  );
};

const CallControls: FC = () => (
  <div className="flex flex-1 flex-col gap-4 border-t border-border-light px-4 py-4">
    {/* <div className="flex flex-1 items-center justify-center">
      (Work in progress.)
    </div>

    <div className="flex gap-3 ">
      <Button
        className="flex-1"
        size={Size.LARGE}
        icon={<PhoneArrowDownLeftIcon className="size-4" />}
        variant={ButtonVariant.CallStart}
        onClick={() => {
          // Will integrate with Twilio Voice JavaScript SDK
        }}
        isDisabled
      >
        Answer
      </Button>
      <Button
        className="flex-1"
        size={Size.LARGE}
        icon={<PhoneXMarkIcon className="size-4" />}
        variant={ButtonVariant.CallEnd}
        onClick={() => {
          // Will integrate with Twilio Voice JavaScript SDK
        }}
        isDisabled
      >
        Hang up
      </Button>
    </div> */}
  </div>
);
